import { useCallback, useState } from 'react';
import { t } from 'i18next';

import { resendAccountCode, verifyAccountCode } from '../api';
import { toErrorMessage } from '../lib/errors';

const CODE_LENGTH = 6;

export function useAccountVerification(userId: string | null | undefined) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = useCallback(async (action: () => Promise<unknown>, fallback: string) => {
    if (!userId) return false;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      return true;
    } catch (cause) {
      setError(toErrorMessage(cause, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  }, [userId]);

  const confirm = useCallback(async () => {
    const ok = await run(
      () => verifyAccountCode(userId as string, code),
      t('That code is not valid. Please try again.'),
    );
    if (ok) setCode('');
    return ok;
  }, [code, run, userId]);

  const resend = useCallback(async (channel: 'EMAIL' | 'SMS') => {
    const ok = await run(
      () => resendAccountCode(userId as string, channel),
      t('We could not resend the code.'),
    );
    if (ok) setNotice(t('Verification code resended!'));
  }, [run, userId]);

  return {
    code,
    setCode,
    busy,
    error,
    notice,
    complete: code.length === CODE_LENGTH,
    codeLength: CODE_LENGTH,
    confirm,
    resend,
  };
}
